// Group by row identifiers, never by product code: repeated products can have different modifiers.
export function getItemGroups(items, allItems = items) {
    const visible = Array.isArray(items) ? items : [];
    const source = Array.isArray(allItems) ? allItems : visible;
    const byId = new Map(
        [...source, ...visible]
            .filter((item) => item?.identifier)
            .map((item) => [item.identifier, item])
    );
    const included = new Set(visible);
    visible.forEach((item) => {
        const seen = new Set([item]);
        let parent = byId.get(item.parent_item);
        while (parent && !seen.has(parent)) {
            included.add(parent);
            seen.add(parent);
            parent = byId.get(parent.parent_item);
        }
    });
    const rows = [...included].sort(
        (a, b) => Number(a.idx || 0) - Number(b.idx || 0)
    );
    const children = new Map();
    const roots = [];
    rows.forEach((item) => {
        const parent = byId.get(item.parent_item);
        if (parent && parent !== item && included.has(parent)) {
            if (!children.has(parent)) children.set(parent, []);
            children.get(parent).push(item);
        } else {
            roots.push(item);
        }
    });
    const visited = new Set();
    const groups = [];
    const addGroup = (root) => {
        if (visited.has(root)) return;
        const entries = [];
        const visit = (item, depth) => {
            if (visited.has(item)) return;
            visited.add(item);
            entries.push({ item, depth, contextOnly: !visible.includes(item) });
            (children.get(item) || []).forEach((child) =>
                visit(child, depth + 1)
            );
        };
        visit(root, 0);
        groups.push({ root, entries });
    };
    roots.forEach(addGroup);
    // Malformed/cyclic relationships must not hide kitchen work.
    rows.forEach(addGroup);
    return groups;
}

export function getItemsQtyTotal(items, allItems = items) {
    return getItemGroups(items, allItems).reduce((sum, { root }) => {
        const parent = String(root.parent_item || "").trim();
        if (parent && parent !== "NULL") return sum;
        const qty = Number(root.qty || 0);
        return sum + (Number.isFinite(qty) ? qty : 0);
    }, 0);
}

export function getOrderTitle(order, fallback) {
    return (
        String(order?.customer_name || order?.customer || "").trim() ||
        order?.table_description ||
        fallback
    );
}

export function getWaiterLabel(t, owner, usersByName) {
    if (!owner) return null;
    const fullName = usersByName?.get?.(owner);
    return t("kds.waiterLabel", { name: fullName || owner });
}

export function getItemNotes(item) {
    const notes = item?.notes ?? item?.item_notes ?? item?.note;
    if (!notes) return "";
    return String(notes).trim();
}

export function isCompletedStatus(status) {
    const normalized = String(status || "")
        .trim()
        .toLowerCase();
    return normalized === "completed" || normalized.startsWith("compl");
}

export function isLikelyDocName(value) {
    return /^[a-f0-9]{10}$/i.test(String(value || "").trim());
}

export function getItemKey(item, suffix = "") {
    return (
        item?.name ||
        item?.identifier ||
        `${item?.item_code || item?.item_name}-${item?.idx || ""}${suffix}`
    );
}

// Scope controls what is displayed, not whether the whole order is complete.
export function hasOutstandingOrderItems(order) {
    return (Array.isArray(order?.entry_items) ? order.entry_items : []).some(
        (item) => !isCompletedStatus(item?.status)
    );
}
